import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Leaf,
    MagnifyingGlass,
    ArrowLeft,
    House,
    Target,
    Binoculars,
    Trophy,
    BookOpen
} from '@phosphor-icons/react';

const ECOSYSTEMS = [
    { id: 'all', label: 'All' },
    { id: 'bush', label: 'Bush' },
    { id: 'reef', label: 'Reef' },
    { id: 'outback', label: 'Outback' }
];

export default function AnimalLibraryPage() {
    const { api } = useAuth();
    const [animals, setAnimals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ecosystem, setEcosystem] = useState('all');
    const [habitat, setHabitat] = useState('all');
    const [habitats, setHabitats] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchAnimals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecosystem]);

    useEffect(() => {
        const loadHabitats = async () => {
            try {
                const res = await api().get('/habitats');
                setHabitats(res.data);
            } catch (err) { /* ignore */ }
        };
        loadHabitats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchAnimals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecosystem]);

    const fetchAnimals = async () => {
        try {
            let url = '/animals';
            if (ecosystem !== 'all') url += `?ecosystem=${ecosystem}`;
            
            const response = await api().get(url);
            setAnimals(response.data);
        } catch (error) {
            toast.error('Failed to load animals');
        } finally {
            setLoading(false);
        }
    };

    const filteredAnimals = animals.filter(animal => {
        const matchesSearch = animal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            animal.species.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesHabitat = habitat === 'all' || (animal.habitat_tags || []).includes(habitat);
        return matchesSearch && matchesHabitat;
    });

    const getStatusColor = (status) => {
        const statusLower = status.toLowerCase();
        if (statusLower.includes('vulnerable') || statusLower.includes('endangered')) {
            return 'bg-[#fb7185] text-white';
        }
        if (statusLower.includes('near threatened')) {
            return 'bg-[#fde047] text-[#1e293b]';
        }
        return 'bg-[#22c55e] text-white';
    };

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <Link 
                            to="/dashboard"
                            className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold"
                            data-testid="back-to-dashboard"
                        >
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Dashboard</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20 px-4 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#1e293b] mb-2">
                        <span className="text-4xl mr-2">🐨</span>
                        Animal Library
                    </h1>
                    <p className="text-[#475569] text-lg">
                        Meet the amazing animals you're helping to protect!
                    </p>
                </motion.div>

                {/* Filters */}
                <motion.div 
                    className="brutalist-card p-4 md:p-6 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    {/* Search */}
                    <div className="relative mb-4">
                        <MagnifyingGlass 
                            weight="bold" 
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#475569]" 
                            size={24} 
                        />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search animals..."
                            className="brutalist-input w-full pl-12"
                            data-testid="search-animals"
                        />
                    </div>

                    {/* Ecosystem Filters */}
                    <div className="flex flex-wrap gap-2 mb-3">
                        {ECOSYSTEMS.map((eco) => (
                            <button
                                key={eco.id}
                                onClick={() => setEcosystem(eco.id)}
                                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                                    ecosystem === eco.id
                                        ? 'bg-[#38bdf8] text-white border-4 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                        : 'bg-white text-[#1e293b] border-2 border-[#1e293b]/20 hover:border-[#38bdf8]'
                                }`}
                                data-testid={`filter-ecosystem-${eco.id}`}
                            >
                                {eco.label}
                            </button>
                        ))}
                    </div>

                    {/* Habitat Filters */}
                    {habitats.length > 0 && (
                        <div>
                            <p className="text-xs font-bold text-[#94a3b8] uppercase tracking-wide mb-2">Filter by Habitat</p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setHabitat('all')}
                                    className={`px-3 py-1.5 rounded-full font-semibold text-xs transition-all ${
                                        habitat === 'all'
                                            ? 'bg-[#22c55e] text-white border-3 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                            : 'bg-white text-[#1e293b] border-2 border-[#1e293b]/20 hover:border-[#22c55e]'
                                    }`}
                                    data-testid="filter-habitat-all"
                                >
                                    All Habitats
                                </button>
                                {habitats.filter(h => h.unlocked).map((h) => (
                                    <button
                                        key={h.id}
                                        onClick={() => setHabitat(h.id)}
                                        className={`px-3 py-1.5 rounded-full font-semibold text-xs transition-all flex items-center gap-1 ${
                                            habitat === h.id
                                                ? 'text-white border-3 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                                : 'bg-white text-[#1e293b] border-2 border-[#1e293b]/20 hover:border-[#22c55e]'
                                        }`}
                                        style={habitat === h.id ? { backgroundColor: h.theme?.accent || '#22c55e' } : {}}
                                        data-testid={`filter-habitat-${h.id}`}
                                    >
                                        <span>{h.emoji}</span> {h.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* Animals Grid */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="font-fredoka text-lg">Loading animals...</p>
                    </div>
                ) : filteredAnimals.length === 0 ? (
                    <div className="brutalist-card p-8 text-center">
                        <span className="text-6xl mb-4 block">🦘</span>
                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">
                            No animals found
                        </h3>
                        <p className="text-[#475569]">
                            Try adjusting your search or filters
                        </p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredAnimals.map((animal, index) => (
                            <motion.div
                                key={animal.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 * index }}
                            >
                                <Link to={`/animals/${animal.id}`} data-testid={`animal-card-${animal.id}`}>
                                    <div className="brutalist-card overflow-hidden h-full group">
                                        <div className="h-48 overflow-hidden relative">
                                            <img 
                                                src={animal.image_url} 
                                                alt={animal.name}
                                                crossOrigin="anonymous"
                                                referrerPolicy="no-referrer"
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                            />
                                            <div className="absolute top-3 right-3">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 border-[#1e293b] ${getStatusColor(animal.conservation_status)}`}>
                                                    {animal.conservation_status}
                                                </span>
                                            </div>
                                            <div className="absolute bottom-3 left-3">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 border-white text-white capitalize ${
                                                    animal.ecosystem === 'bush' ? 'bg-[#22c55e]' :
                                                    animal.ecosystem === 'reef' ? 'bg-[#38bdf8]' :
                                                    'bg-[#f59e0b]'
                                                }`}>
                                                    {animal.ecosystem}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <h3 className="font-fredoka font-bold text-xl text-[#1e293b] mb-1">
                                                {animal.name}
                                            </h3>
                                            <p className="text-sm text-[#475569] italic mb-3">
                                                {animal.species}
                                            </p>
                                            <p className="text-sm text-[#475569] line-clamp-2">
                                                {animal.description}
                                            </p>
                                            <p className="text-[#22c55e] font-semibold text-sm mt-3">
                                                Learn More →
                                            </p>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
